import Env from 'src/environmentVariables/EnvironmentVariables';
import {
	ENUM_UTILS,
	IPredictionMarketConfig,
	SerializedPredictionMarketConfig,
} from '@drift/common';
import {
	MarketStatus,
	MAX_PREDICTION_PRICE,
	PerpMarketAccount,
	ZERO,
} from '@drift-labs/sdk';
import dayjs from 'dayjs';
import { S3_BUCKET_DRIFT_PUBLIC_MAINNET } from 'src/constants/constants';

const DEFAULT_ENDING_SOON_THRESHOLD_DAYS = 3;

export enum PREDICTION_MARKET_CATEGORY {
	ALL = 'All',
	US_ELECTIONS = 'US Elections',
	FORMULA_1 = 'F1',
	SPORTS = 'Sports',
}

export type PredictionMarketCategoryConfig = {
	label: string;
	imgSrc: string;
	comingSoon?: boolean;
	backgroundSrc?: string;
	summary?: React.ReactNode;
	hide?: boolean;
};

enum ResolutionOutcome {
	Unresolved,
	Yes,
	No,
}

export class PredictionMarketConfig implements IPredictionMarketConfig {
	title: string;
	shortTitle: string;
	resolutionDate: Date;
	category?: PREDICTION_MARKET_CATEGORY;
	symbol: string;
	previewImgSrc?: string;
	devnetMarketIndex?: number;
	mainnetMarketIndex?: number;
	resolutionDateUnknown?: boolean;
	newUntilTs?: number;

	/**
	 * Days before resolution date to show ending soon label
	 */
	endingSoonThresholdDays?: number;
	production?: boolean;

	constructor(config: SerializedPredictionMarketConfig) {
		this.title = config.title;
		this.shortTitle = config.shortTitle;
		this.resolutionDate = new Date(config.resolutionDateUnix);
		this.category = config.category as PREDICTION_MARKET_CATEGORY;
		this.symbol = config.symbol;
		this.previewImgSrc = config.previewImgSrc;
		this.devnetMarketIndex = config.devnetMarketIndex;
		this.mainnetMarketIndex = config.mainnetMarketIndex;
		this.endingSoonThresholdDays = config.endingSoonThresholdDays;
		this.resolutionDateUnknown = config.resolutionDateUnknown;
		this.newUntilTs = config.newUntilTs;
		this.production = config.production;
	}

	/**
	 * Returns true if the market is live on the current network.
	 */
	get isLive() {
		if (Env.sdkEnv === 'devnet') {
			return this.devnetMarketIndex !== undefined;
		} else {
			return this.mainnetMarketIndex !== undefined;
		}
	}

	get marketIndex() {
		const marketIndexToUse =
			Env.sdkEnv === 'mainnet-beta'
				? this.mainnetMarketIndex
				: this.devnetMarketIndex;

		if (marketIndexToUse === undefined) {
			console.error(`Market index not found for ${this.symbol}`);
			return undefined;
		}

		return marketIndexToUse;
	}

	get isEndingSoon() {
		return dayjs(this.resolutionDate).isBefore(
			dayjs().add(
				this.endingSoonThresholdDays ?? DEFAULT_ENDING_SOON_THRESHOLD_DAYS,
				'day'
			)
		);
	}

	isNew() {
		if (!this.newUntilTs) return false;

		return dayjs().isBefore(dayjs(this.newUntilTs));
	}

	get imgSrc() {
		return `${S3_BUCKET_DRIFT_PUBLIC_MAINNET}/prediction-markets/markets/${this.symbol}.webp`;
	}

	static isResolving(perpMarketAccount?: PerpMarketAccount) {
		return ENUM_UTILS.match(
			perpMarketAccount?.status,
			MarketStatus.REDUCE_ONLY
		);
	}

	static isResolved(perpMarketAccount?: PerpMarketAccount) {
		return (
			ENUM_UTILS.match(perpMarketAccount?.status, MarketStatus.SETTLEMENT) ||
			ENUM_UTILS.match(perpMarketAccount?.status, MarketStatus.DELISTED)
		);
	}

	static isActive(perpMarketAccount?: PerpMarketAccount) {
		return ENUM_UTILS.match(perpMarketAccount?.status, MarketStatus.ACTIVE);
	}

	static getResolvedOutcome(perpMarketAccount?: PerpMarketAccount) {
		if (
			!perpMarketAccount ||
			!PredictionMarketConfig.isResolved(perpMarketAccount)
		)
			return ResolutionOutcome.Unresolved;

		const tickSize = perpMarketAccount.amm.orderTickSize;

		// markets do not always resolved to exactly $0 or $1, there is usually a buffer of up to a tick size
		if (perpMarketAccount.expiryPrice.gte(MAX_PREDICTION_PRICE.sub(tickSize))) {
			return ResolutionOutcome.Yes;
		}

		if (perpMarketAccount.expiryPrice.lte(ZERO.add(tickSize))) {
			return ResolutionOutcome.No;
		}

		return ResolutionOutcome.Unresolved;
	}

	public serialize(): SerializedPredictionMarketConfig {
		return {
			title: this.title,
			shortTitle: this.shortTitle,
			resolutionDateUnix: this.resolutionDate.getTime(),
			category: this.category,
			symbol: this.symbol,
			previewImgSrc: this.previewImgSrc,
			devnetMarketIndex: this.devnetMarketIndex,
			mainnetMarketIndex: this.mainnetMarketIndex,
			endingSoonThresholdDays: this.endingSoonThresholdDays,
			imgSrc: this.imgSrc,
			production: this.production,
		};
	}
}
